import clsx from 'clsx';
import { useState, useRef } from 'react';
import { HTMLAttributes } from 'react';
import Icon from '../icon/icon';
import Text from '../text/text';

export type InputDropzoneProps = {
    onChange?: (files: File[]) => void;
    loading?: {
        active?: boolean;
        text?: string;
        /*         icon?: string;
         */
    };
    texts?: {
        title?: string;
        supportedFormatsLabel?: string;
        buttonText?: string;
    };
    icon?: {
        name?: string;
        color?: string;
        className?: string;
    };
    img?: React.ReactNode;
    inputProps?: {
        accept?: string;
        capture?: boolean | 'user' | 'environment';
        multiple?: boolean;
        validateFiles?: boolean;
    } & React.InputHTMLAttributes<HTMLInputElement>;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'onClick' | 'onDragOver' | 'onDragLeave' | 'onDrop'>;

export function InputDropzone({
    onChange,
    loading = {
        active: false,
        text: '',
        /* icon: '', */
    },
    icon = {
        name: 'ri-image-line',
        color: 'text-[#7886cb]',
        className: '',
    },
    img = undefined,
    texts = {
        title: 'Arrastre y suelte un archivo aquí',
        supportedFormatsLabel: 'Formatos soportados:',
        buttonText: 'Seleccione un archivo',
    },
    inputProps = {
        accept: 'application/pdf,image/jpeg,image/png,image/jpg',
        capture: false,
        multiple: false,
        validateFiles: true,
    },
    className,
    ...props
}: InputDropzoneProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (inputProps?.disabled) {
            return;
        }

        setDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (inputProps?.disabled) {
            return;
        }

        setDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);

        if (inputProps?.disabled) {
            return;
        }

        if (e.dataTransfer.files.length) {
            const files = Array.from(e.dataTransfer.files).filter((file) => {
                // Filter files based on the accepted types
                if (inputProps?.validateFiles || inputProps?.validateFiles === undefined) {
                    const acceptedTypes = inputProps?.accept
                        ? inputProps?.accept.split(',').map((type) => type.trim())
                        : [];
                    return (
                        acceptedTypes.length === 0 ||
                        acceptedTypes.includes(file.type) ||
                        acceptedTypes.includes(file.name.split('.').pop() || '')
                    );
                }

                return inputProps?.accept?.split(',').map((type) => type.trim());
            });
            onChange?.(Array.from(files));
        }
    };

    return (
        <div>
            <input
                {...inputProps}
                ref={fileInputRef}
                disabled={inputProps?.disabled}
                id="file-input"
                name="file"
                type="file"
                data-test-id="dropzone-input"
                accept={inputProps?.accept}
                className={'hidden' + (inputProps?.className ? ` ${inputProps?.className}` : '')}
                onChange={(e) => {
                    const file = e.target.files;
                    if (file) {
                        onChange?.(Array.from(file));
                    }
                }}
            />
            <div
                {...props}
                className={`border border-dashed rounded-lg p-6 flex flex-col gap-4 items-center ${
                    inputProps?.disabled
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                        : dragging
                        ? 'border-primary-700 bg-blue-50'
                        : 'border-gray-300'
                } p-4 text-center  ${className}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {loading.active ? (
                    <div className="h-[168px] flex flex-col items-center justify-center">
                        <Text
                            size="16"
                            weight="medium"
                            family="poppins"
                            className={`${inputProps?.disabled ? 'text-gray-400' : 'text-gray-800'} mb-2`}
                        >
                            {loading.text}
                        </Text>
                    </div>
                ) : (
                    <>
                        <div
                            className={`rounded-full shadow-[0_6px_10px_0_rgba(20,115,247,0.15)] w-[68px] aspect-square bg-white mx-auto mb-2 flex items-center justify-center ${
                                inputProps?.disabled ? 'opacity-60' : ''
                            }`}
                        >
                            {img ? (
                                <div className="w-full h-full flex items-center justify-center">{img}</div>
                            ) : (
                                <Icon
                                    name={icon.name}
                                    className={clsx(
                                        `${inputProps?.disabled ? 'text-gray-400' : icon.color}`,
                                        icon.className,
                                    )}
                                ></Icon>
                            )}
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <Text
                                className={`${inputProps?.disabled ? 'text-gray-400' : 'text-gray-800'}`}
                                size="16"
                                weight="medium"
                                family="poppins"
                            >
                                {texts.title}
                            </Text>
                            <Text
                                size="12"
                                weight="normal"
                                family="roboto"
                                className={`${inputProps?.disabled ? 'text-gray-400' : 'text-gray-500'}`}
                            >
                                <>{texts.supportedFormatsLabel} </>
                            </Text>
                        </div>
                        <label
                            htmlFor="file-input"
                            className={`text-sm font-semibold leading-none line border py-1 px-4 rounded-[4px] flex items-center justify-center gap-2 ${
                                inputProps?.disabled
                                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                    : 'border-primary-700 text-primary-700 cursor-pointer hover:bg-primary-50'
                            }`}
                        >
                            <Icon
                                size={16}
                                name="ri-file-2-line"
                                className={`font-normal ${inputProps?.disabled ? 'text-gray-400' : 'text-primary-700'}`}
                            ></Icon>
                            <span>{texts.buttonText}</span>
                        </label>
                    </>
                )}
            </div>
        </div>
    );
}
